import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import styles from './PCTMap.module.css';

// Fix for Leaflet default marker icon in React
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Create custom pane for location marker
const MarkerPane = ({ name, zIndex }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!map.getPane(name)) {
      map.createPane(name);
      map.getPane(name).style.zIndex = zIndex;
    }
  }, [map, name, zIndex]);
  
  return null;
};

const PCTMap = ({ milesCovered, totalMiles, latestUpdate, updates = [] }) => {
  const [trailCoordinates, setTrailCoordinates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHighRes, setIsLoadingHighRes] = useState(false);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  
  // Format date for display in popups
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Get center coordinates based on the latest update
  const getCenter = () => {
    if (latestUpdate && latestUpdate.location_coordinates) {
      // Return latest update coordinates with offset for vertical centering
      return [
        latestUpdate.location_coordinates.lat - 0.8, // Smaller offset for better centering
        latestUpdate.location_coordinates.lng
      ];
    } else if (trailCoordinates.length > 0) {
      // Fall back to trail midpoint
      return [
        (trailCoordinates[0][0] + trailCoordinates[trailCoordinates.length - 1][0]) / 2,
        (trailCoordinates[0][1] + trailCoordinates[trailCoordinates.length - 1][1]) / 2
      ];
    } else {
      // Default center if nothing else is available
      return [40.0, -120.0];
    }
  };
  
  // Log the latest update coordinates to console
  useEffect(() => {
    if (latestUpdate && latestUpdate.location_coordinates) {
      console.log('Latest update with coordinates:', latestUpdate);
      console.log('Coordinates:', latestUpdate.location_coordinates);
      
      // If map is already initialized, fly to the latest update location
      if (mapRef.current) {
        const map = mapRef.current;
        const center = getCenter();
        map.setView(center, 7); // Fixed zoom level for consistent view
      }
    } else {
      console.log('No update with coordinates available');
    }
  }, [latestUpdate]);
  
  // Function to parse GPX data
  const parseGpxData = (gpxData) => {
    const parser = new DOMParser();
    const gpx = parser.parseFromString(gpxData, "text/xml");
    
    const trackPoints = gpx.querySelectorAll('trkpt');
    console.log(`Found ${trackPoints.length} track points in GPX data`);
    
    return Array.from(trackPoints).map(point => [
      parseFloat(point.getAttribute('lat')),
      parseFloat(point.getAttribute('lon'))
    ]);
  };
  
  // Fetch GPX data function
  const fetchGpxFile = async (filePath) => {
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch GPX data: ${response.status} ${response.statusText}`);
    }
    return await response.text();
  };
  
  useEffect(() => {
    // First load the low-resolution data for quick rendering
    const loadLowResTrail = async () => {
      setIsLoading(true);
      try {
        const gpxData = await fetchGpxFile('/data/pct_small.gpx');
        const coordinates = parseGpxData(gpxData);
        setTrailCoordinates(coordinates);
        setError(null);
      } catch (error) {
        console.error("Error loading low-res GPX data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
        
        // After low-res is loaded, start loading high-res version
        loadHighResTrail();
      }
    };
    
    // Then load the high-resolution data
    const loadHighResTrail = async () => {
      setIsLoadingHighRes(true);
      try {
        const gpxData = await fetchGpxFile('/data/pct.gpx');
        const coordinates = parseGpxData(gpxData);
        setTrailCoordinates(coordinates);
      } catch (error) {
        console.error("Error loading high-res GPX data:", error);
        // Don't set error since we already have low-res data displayed
      } finally {
        setIsLoadingHighRes(false);
      }
    };
    
    loadLowResTrail();
  }, []);
  
  return (
    <div>
      {error && <div style={{ color: 'red' }}>Error: {error}</div>}
      
      <MapContainer 
        center={getCenter()} 
        zoom={7} 
        scrollWheelZoom={true}
        className={styles.mapContainer}
        ref={mapRef}
      >
        <TileLayer
          attribution='Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>'
          url="https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}"
        />
        
        {/* Create custom pane for the markers */}
        <MarkerPane name="markerPane" zIndex={650} />
        <MarkerPane name="previousUpdatesPane" zIndex={450} />
        
        {/* Draw the PCT Trail using GPX data */}
        {trailCoordinates.length > 0 && (
          <Polyline 
            positions={trailCoordinates}
            color="#ff8b17"
            weight={4}
            opacity={0.9}
            zIndex={0}
          />
        )}
        
        {/* Show all previous updates as small red dots */}
        {updates && updates.length > 0 && updates.map((update) => {
          if (update && update.location_coordinates && 
              update.location_coordinates.lat && 
              update.location_coordinates.lng) {
            return (
              <CircleMarker 
                key={update._id}
                center={[
                  update.location_coordinates.lat, 
                  update.location_coordinates.lng
                ]}
                radius={6}
                pathOptions={{
                  fillColor: '#FF2222',
                  fillOpacity: 1.0,
                  color: 'white',
                  weight: 2
                }}
                pane="previousUpdatesPane"
              >
                <Popup>
                  <div className={styles.updatePopup}>
                    <h3>{update.title || update.location_name || `Mile ${update.mile}`}</h3>
                    <p>{formatDate(update.time)}</p>
                    <Link to={`/posts/${update._id}`}>View Details</Link>
                  </div>
                </Popup>
              </CircleMarker>
            );
          }
          return null;
        })}
        
        {/* Show circle marker for latest update with location */}
        {latestUpdate && latestUpdate.location_coordinates && (
          <CircleMarker 
            center={[
              latestUpdate.location_coordinates.lat, 
              latestUpdate.location_coordinates.lng
            ]}
            radius={10}
            pathOptions={{
              fillColor: '#1a75ff',
              fillOpacity: 1.0,
              color: 'white',
              weight: 2
            }}
            pane="markerPane"
          >
            <Popup>Latest Update</Popup>
          </CircleMarker>
        )}
      </MapContainer>
    </div>
  );
};

export default PCTMap; 